import { IconButton, Paper, Typography } from "@mui/material";
import React from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { type DbComment } from "../../requests/v2/comments";
import { User } from "../../requests/users";

export type CommentPaperProps = {
  comment: DbComment;
  user: User | null | undefined;
  getUser: (userId: number) => User | undefined;
  isEditingOrAdding: boolean;
  setAddOrEditContent: (content: string) => void;
  setEditingComment: (comment: DbComment | null) => void;
  onAttemptDelete: (comment: DbComment) => void;
};

const formatDateTime = (date: Date) => {
  const now = new Date();
  const isWithinLastYear = date > new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());

  const formattedDate = date.toLocaleString(
    "en-US",
    isWithinLastYear
      ? { month: "short", day: "numeric" }
      : { year: "numeric", month: "short", day: "numeric" }
  );
  const formattedTime = date.toLocaleString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

  return `${formattedDate} · ${formattedTime}`;
};

export const CommentPaper = ({
  comment,
  user,
  getUser,
  isEditingOrAdding,
  setAddOrEditContent,
  setEditingComment,
  onAttemptDelete,
}: CommentPaperProps) => {
  const author = comment.createdBy;
  const authorName = getUser(author)?.name;

  return (
    <Paper
      elevation={3}
      sx={{
        m: 1,
        p: "0.5em",
        position: "relative",
      }}
    >
      <Typography variant="subtitle2" color="primary">
        {authorName}
        {author === user?.id && (
          <>
            <IconButton
              sx={{ p: "2px", mb: "2px" }}
              disabled={isEditingOrAdding}
              onClick={() => {
                setAddOrEditContent(comment.content);
                setEditingComment(comment);
              }}
            >
              <EditIcon sx={{ fontSize: 16 }} color={isEditingOrAdding ? undefined : "primary"} />
            </IconButton>
            <IconButton sx={{ p: "2px", mb: "2px" }} onClick={() => onAttemptDelete(comment)}>
              <DeleteIcon sx={{ fontSize: 16 }} color="error" />
            </IconButton>
          </>
        )}
      </Typography>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{
          position: "absolute",
          top: "0.8em",
          right: "0.8em",
        }}
      >
        {formatDateTime(comment.createdAt)}
      </Typography>
      <Typography variant="body1">{comment.content}</Typography>
    </Paper>
  );
};
